/**
 * Hooks de dados do editor de Personas (Brief 4 · Tela 2).
 *
 * Consome o CRUD real (apps/engine/src/modules/personas):
 *   - GET /v1/personas      (lista)   · gate `persona-read`
 *   - GET /v1/personas/:id  (detalhe) · gate `persona-read`
 *
 * As queries são GATEADAS por capability no `enabled` (não só no render) —
 * sem `persona-read` o fetch nem dispara, evitando 403 vazando em toast
 * (lição "gatear o fetch, não só o render"). A edição (PATCH · gate
 * `persona-author`) chega via `useUpdatePersona`; criar/arquivar nos PRs seguintes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';
import { CONDUCT_PROFILE_QUERY_KEY } from '../conduct/use-conduct-profile';

import type { CreatePersonaBody, PersonaDto, UpdatePersonaBody } from './types';
import type { UseMutationResult } from '@tanstack/react-query';

const LIST_QUERY_KEY = 'personas-list';
const DETAIL_QUERY_KEY = 'persona-detail';
const STALE_TIME = 60_000;

export function usePersonaList(): {
  data: PersonaDto[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('persona-read');
  const query = useQuery({
    queryKey: [LIST_QUERY_KEY],
    queryFn: () => api.listPersonas(),
    enabled: canRead,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

export function usePersona(id: string | null): {
  data: PersonaDto | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const canRead = useCapability('persona-read');
  const query = useQuery({
    queryKey: [DETAIL_QUERY_KEY, id],
    queryFn: () => api.getPersona(id!),
    enabled: canRead && id !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Mutation de criação da persona (POST /v1/personas · gate `persona-author`).
 *
 * Em sucesso: grava o DTO retornado no cache do detalhe (já como `active`, version 1)
 * e invalida a lista — o chamador navega para o editor da nova persona. Erros (incl.
 * 409 de nome duplicado) propagam ao chamador. Espelha `useCreatePlaybook`
 * (Brief 4 · Tela 1).
 */
export function useCreatePersona(): UseMutationResult<PersonaDto, unknown, CreatePersonaBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createPersona(body),
    onSuccess: (created) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, created.id], created);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}

/**
 * Mutation de edição da persona (PATCH /v1/personas/:id · gate `persona-author`).
 *
 * Em sucesso: grava o DTO retornado no cache do detalhe (já com `version`
 * incrementada para o próximo If-Match) e invalida a lista. Erros (incl. 409 de
 * optimistic lock) propagam para o chamador tratar — a view exibe banner de
 * conflito não-destrutivo + recarregar (nunca sobrescreve cego). Espelha
 * `useUpdatePlaybook` (Brief 4 · Tela 1).
 */
export function useUpdatePersona(
  id: string,
): UseMutationResult<PersonaDto, unknown, { expectedVersion: number; body: UpdatePersonaBody }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expectedVersion, body }) => api.updatePersona(id, expectedVersion, body),
    onSuccess: (updated) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, id], updated);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
      // ★ Fase 2 «Conduta e Contexto» — invalida a RESOLUÇÃO da conduta (efetivo + origem
      // por eixo). Achado na validação viva no nível Tenant: sem isto o «Hoje: …» de cada
      // eixo segue mostrando o valor ANTIGO depois de salvar, porque esta mutação só
      // reescreve o cache da própria entidade. Tela de conduta com efetivo velho é
      // exatamente a desonestidade que esta frente existe para matar.
      void queryClient.invalidateQueries({ queryKey: [CONDUCT_PROFILE_QUERY_KEY] });
    },
  });
}

/**
 * Mutation de ciclo de vida — descontinuar (DELETE /v1/personas/:id · gate
 * `persona-author`). Soft-delete → `status: deprecated` (preserva referências de
 * playbooks); If-Match obrigatório (ADR-071); HTTP 204 sem corpo. Não há reativar
 * via API (R15 ao vivo). Em sucesso invalida o detalhe (a persona recarrega como
 * `deprecated`, version++) e a lista. 409 (versão divergente) propaga ao chamador.
 * Espelha `useArchivePlaybook` (Brief 4 · Tela 1).
 */
export function useDeprecatePersona(
  id: string,
): UseMutationResult<void, unknown, { expectedVersion: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expectedVersion }) => api.deprecatePersona(id, expectedVersion),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [DETAIL_QUERY_KEY, id] });
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}
